import Big from 'big.js';
import { DateTime } from 'luxon';
import Batch from 'smg/batch';
import * as buttons from 'smg/buttons';
import { GetLineItems } from 'smg/customer-api/v2/lineitems';
import { OpenOrderInvoice } from 'smg/customer-api/v2/orders';
import * as models from 'smg/customer-api/models';
import { Orders } from 'smg/customer-api/orders';
import * as session from 'smg/customer-api/session';
import { ShowError } from 'smg/errors';
import { GetProofURLs } from 'smg/proofs';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { PageLoader } from '../account';
import { AccountReady } from '../account-session';
import * as carts from '../carts';
import * as payments from '../payments';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const user = await session.Session().then(s => s.user);

    const b = new Batch();

    const carriers = b.get('/carriers', {
        params: {
            'data': 'name,link',
        },
    }).then(resp => {
        const data = models.CarriersHyejooResponse.fromJSON(resp.data);
        const m = new Map<number, models.Carrier>();
        for (const carrier of data.carriers) {
            m.set(carrier.id, carrier);
        }
        return m;
    });

    const lineItems = GetLineItems({ data: 'name,pluralName,product,shipping' });

    void b.exec();

    const pl: PageLoader<models.HyejooOrder> = new PageLoader<models.HyejooOrder>({
        get: () => Orders({
            sort: pl.sort(),
            max: pl.currentPageSize(),
            data: 'invoiceNumber,created,orderLineItems,orderProofs,orderShippingOptions,orderPayments,total,paid,balance,totalSansTax,orderStatusID,key',
            p: pl.currentPage(),
            q: pl.search(),
        }).then(data => ({ rows: data.results, nextPage: data.nextPage })),
        draw: async order => {
            const el = template.Clone('#order-template').firstElementChild as HTMLElement;

            const invoiceNumber = el.querySelector('.order-invoice-number') as HTMLElement;
            invoiceNumber.textContent = String(order.invoiceNumber);

            const viewSummary = el.querySelector('#viewSummary') as HTMLAnchorElement;
            viewSummary.href += String(order.invoiceNumber);

            const date = el.querySelector('.order-date') as HTMLElement;
            if (order.created.isValid) {
                date.textContent = order.created.toLocaleString(DateTime.DATE_SHORT);
            }

            const total = order.total;
            const totalSansTax = order.totalSansTax;
            let shipping = Big(0);
            for (const ol of order.orderLineItems) {
                if (!ol.lineItemID) {
                    continue;
                }

                const lineItem = (await lineItems).get(ol.lineItemID);
                if (!lineItem?.shipping) {
                    continue;
                }

                shipping = shipping.add(ol.price.mul(ol.quantity));
            }
            const balance = order.balance;
            const tax = total.sub(totalSansTax);
            const subtotal = totalSansTax.sub(shipping);

            const subtotalEls = el.querySelectorAll('.order-subtotal');
            subtotalEls.forEach(el => el.textContent = DollarFormat(subtotal));

            const shippingEls = el.querySelectorAll('.order-shipping');
            shippingEls.forEach(el => el.textContent = DollarFormat(shipping));

            const taxEls = el.querySelectorAll('.order-tax');
            taxEls.forEach(el => el.textContent = DollarFormat(tax));

            const totalEls = el.querySelectorAll('.order-total');
            totalEls.forEach(el => el.textContent = DollarFormat(total));

            const balanceEls = el.querySelectorAll('.order-balance');
            balanceEls.forEach(el => el.textContent = DollarFormat(balance));

            const balanceContainers = el.querySelectorAll('.order-balance-container');
            const makePayment = el.querySelector('.order-make-payment') as HTMLElement;
            if (balance.gt('0.05')) {
                balanceContainers.forEach(el => el.classList.add('text-danger'));
                makePayment.addEventListener('click', () => payments.PromptPayment(user, [order.id], balance, false).then(() => pl.load()));
            } else {
                makePayment.parentElement?.removeChild(makePayment);
            }

            const reorder = el.querySelector('.order-reorder') as HTMLButtonElement;
            // $0 reorders are rejected server-side (#7049), so drop the button instead of wiring a dead click handler
            if (total.eq(0)) {
                reorder.parentElement?.removeChild(reorder);
            } else {
                reorder.addEventListener('click', () => {
                    buttons.Start(reorder);
                    void carts.AddCartItem({ orderID: order.key }).then(() => {
                        reorder.classList.remove('btn-primary', 'btn-success', 'text-white');
                        reorder.classList.add('btn-outline-success');

                        const $icon = reorder.querySelector('.material-symbols-sharp');
                        if ($icon) {
                            $icon.textContent = 'check';
                        }
                    }).finally(() => buttons.Stop(reorder));
                });
            }

            const invoice = el.querySelector('.order-invoice') as HTMLButtonElement;
            invoice.addEventListener('click', () => {
                buttons.Start(invoice);
                void OpenOrderInvoice(`id:${order.id ?? ''}`).catch(ShowError).finally(() => buttons.Stop(invoice));
            });

            const proofsContainer = el.querySelector('.order-proofs-container') as HTMLElement;
            const proofsParent = el.querySelector('.order-proofs-parent') as HTMLElement;
            if (order.orderProofs.length) {
                for (const op of order.orderProofs) {
                    const p = op.proof;
                    if (!p) {
                        continue;
                    }

                    const urls = GetProofURLs(window.cloudFront, p.file);

                    const a = template.Clone('#order-proof-template').firstElementChild as HTMLAnchorElement;
                    a.href = urls.main;
                    a.setAttribute('data-fancybox', order.id);

                    const img = a.querySelector('img') as HTMLImageElement;
                    img.src = urls.thumb;
                    img.alt = op.name;

                    proofsParent.appendChild(a);
                }
            } else {
                proofsContainer.parentElement?.removeChild(proofsContainer);
            }

            let shipmentsCount = 0;
            let delivered = false;
            const trackingParent = el.querySelector('.order-tracking-parent') as HTMLElement;
            for (const orderShippingOption of order.orderShippingOptions) {
                for (const orderShippingOptionShipment of orderShippingOption.orderShippingOptionShipments) {
                    const shipment = orderShippingOptionShipment.shipment;
                    if (!shipment) {
                        continue;
                    }

                    if (shipment.deliveredAt.isValid) {
                        delivered = true;
                    }

                    if (shipmentsCount++) {
                        trackingParent.appendChild(document.createTextNode(', '));
                    }

                    const carrier = (await carriers).get(shipment.carrierID);

                    if (carrier) {
                        const a = document.createElement('a') as HTMLAnchorElement;
                        a.href = carrier.trackingURL(shipment.trackingNumber);
                        a.textContent = shipment.trackingNumber;

                        trackingParent.appendChild(a);
                    } else {
                        trackingParent.appendChild(document.createTextNode(shipment.trackingNumber));
                    }
                }
            }
            if (!shipmentsCount) {
                const trackingContainer = el.querySelector('.order-tracking-container');
                trackingContainer?.parentElement?.removeChild(trackingContainer);
            }

            const orderLineItemStages: models.OrderLineItemStage[] = [];

            if (order.orderLineItems.length) {
                const qtys: Record<string, number> = {};
                for (const ol of order.orderLineItems) {
                    if (ol.orderLineItemStages.length) {
                        orderLineItemStages.push(...ol.orderLineItemStages);
                    }

                    if (!ol.quantity) {
                        continue;
                    }

                    const lineItem = (await lineItems).get(ol.lineItemID);

                    if (!(ol.lineItem || lineItem?.product)) {
                        continue;
                    }

                    const name = ol.lineItem || lineItem?.pluralName || lineItem?.name || 'Other';
                    qtys[name] = (qtys[name] || 0) + ol.quantity;
                }

                const summary = el.querySelector('.order-summary') as HTMLElement;
                summary.textContent = Object.keys(qtys).map(n => `${qtys[n]} ${n}`).join(', ');
            }

            const orderStatusEl = el.querySelector('.order-status') as HTMLElement;
            let orderStatus: string | undefined;
            if (delivered) {
                orderStatus = 'Delivered';
            } else if (shipmentsCount !== 0) {
                orderStatus = 'Shipped';
            } else if (orderLineItemStages.length !== 0) {
                orderLineItemStages.sort((a, b) => b.created.toMillis() - a.created.toMillis());
                if (orderLineItemStages[0].stage) {
                    orderStatus = orderLineItemStages[0].stage.name;
                }
            } else if (order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_CONFIRMED) {
                orderStatus = 'In production';
            } else if (order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_UNCONFIRMED) {
                orderStatus = 'Preparing for production';
            } else if (order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_ORDER) {
                orderStatus = 'Order received';
            }

            orderStatusEl.textContent = orderStatus || '';

            return el;
        },
    });

    pl.load();
}
